import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { CandidDetail } from '@/api/types/photo'
import type { PortraitDetail } from '@/api/types/portrait'
import { useCandidDetail } from '@/api/hooks/use-candid-detail'
import { usePhotoCandidFromCache } from '@/api/hooks/use-photo-candid-from-cache'
import { usePhotoDetailPlacementRefresh } from '@/api/hooks/use-photo-detail-placement-refresh'
import { usePhotoPlacementOverrides } from '@/api/hooks/use-photo-placement-overrides'
import { usePhotoPlacementSides } from '@/api/hooks/use-photo-placement-sides'
import { usePortraitDetail } from '@/api/hooks/use-portrait-detail'
import { useSpreadsQuery } from '@/api/hooks/use-spreads'
import {
  isPortraitPlacedPhoto,
  resolvePlacedPhotoIdentity,
} from '@/lib/photos/fill-shape-with-photo'
import { formatPlacedPages, resolvePlacedPages } from '@/lib/photos/placed-pages'

/** The printed page numbers the server lists, or `undefined` while its details have not loaded. */
function serverPageNumbers(
  isPortraitSource: boolean,
  detail: CandidDetail | undefined,
  portraitDetail: PortraitDetail | undefined
): number[] | undefined {
  const usage = isPortraitSource
    ? portraitDetail && (portraitDetail.portraitPageUsageList ?? [])
    : detail && (detail.candidPageUsage ?? [])

  return usage?.map((page) => page.pageNumber)
}

/**
 * The left Photos panel's Placed field: the printed page numbers the photo is on ("13, 46, 101"),
 * "Not placed" for none, or a flag-only fallback ("Yes", "Not placed", "—") while the page list
 * can't be worked out yet.
 *
 * The server's page list (the portrait endpoint for a portrait fill, the candid one otherwise) has
 * this session's own placements laid over it, like the trays' placed flags: the server only
 * catches up on its own schedule, so a photo placed, moved or removed here must show at once. The
 * details are re-fetched (debounced) after this photo's pages change. Only the panel reads this, so
 * the canvas toolbar and context menu, which share `usePhotoPanelContext`, don't pay for it.
 */
export function usePhotoPlacedLabel(selectedPhotoShape: ShapeData | undefined): string {
  const imageId = selectedPhotoShape
    ? resolvePlacedPhotoIdentity(selectedPhotoShape)?.imageId
    : null
  const isPortraitSource = selectedPhotoShape ? isPortraitPlacedPhoto(selectedPhotoShape) : false
  const candid = usePhotoCandidFromCache(imageId ?? undefined)
  const { data: detail } = useCandidDetail(imageId ?? null)
  // A placed portrait's id is the roster's `portraitId`, whose page list lives on the portrait
  // endpoint, not the candid one.
  const { data: portraitDetail } = usePortraitDetail(isPortraitSource ? (imageId ?? null) : null)
  const placementOverrides = usePhotoPlacementOverrides()
  const placementSides = usePhotoPlacementSides()
  const { data: spreads } = useSpreadsQuery()
  const sessionSides = imageId ? placementSides.get(imageId) : undefined

  usePhotoDetailPlacementRefresh(imageId ?? null, isPortraitSource, sessionSides)

  const pages = resolvePlacedPages({
    serverPages: serverPageNumbers(isPortraitSource, detail, portraitDetail),
    sidesBySpread: sessionSides,
    spreads,
  })

  if (pages) return formatPlacedPages(pages)

  // No page list yet (details not loaded, or the book's spreads are unknown): fall back to the
  // placed flag alone.
  const overrideState = imageId ? placementOverrides.get(imageId) : undefined

  if (overrideState === true) return 'Yes'
  if (overrideState === false) return 'Not placed'
  if (candid) return candid.placedFlag ? 'Yes' : 'Not placed'

  return '—'
}
